import { invariantResponse } from '@epic-web/invariant'
import { json, type LoaderFunctionArgs } from '@remix-run/node'
import { Link, useLoaderData, type MetaFunction } from '@remix-run/react'
import { brandName } from '#app/components/branding.js'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { getUserImgSrc } from '#app/utils/misc.tsx'
import { getServerAndShardList } from '#app/utils/shards.server.js'

export async function loader({ params }: LoaderFunctionArgs) {
	const owner = await prisma.user.findFirst({
		select: {
			id: true,
			name: true,
			username: true,
			image: { select: { id: true } },
			shards: { select: { id: true, shard: true } },
		},
		where: { username: params.username },
	})

	invariantResponse(owner, 'Owner not found', { status: 404 })

	const getServers = await getServerAndShardList()
	const serverDeets = getServers.filter((server) =>
		server.shards.find((shard) => owner.shards.find((s) => s.shard?.id === shard.id)?.id),
	)

	const userShards = owner.shards.map((shard) => ({
		...shard,
		server: serverDeets.find((server) => server.shards.find((s) => s.id === shard.shard?.id)),
	}))

	return json({ owner, userShards })
}

export default function ShardsRoute() {
	const { owner, userShards } = useLoaderData<typeof loader>()
	const ownerDisplayName = owner.name ?? owner.username

	return (
		<div className="container mx-auto mb-24 px-4 pt-8 md:mb-48">
			{/* Header */}
			<div className="flex items-center gap-3">
				<Link to={`/users/${owner.username}`}>
					<img
						src={getUserImgSrc(owner.image?.id)}
						alt={ownerDisplayName}
						className="h-10 w-10 rounded-full object-cover md:h-12 md:w-12"
					/>
				</Link>
				<div>
					<Link to={`/users/${owner.username}`} className="text-sm text-muted-foreground hover:underline">
						&larr; {ownerDisplayName}
					</Link>
					<h1 className="text-xl font-bold md:text-2xl">Shards ({userShards.length})</h1>
				</div>
			</div>

			{/* Shards list */}
			<div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
				{userShards.length === 0 && (
					<p className="col-span-full text-sm text-muted-foreground">No shards listed.</p>
				)}
				{userShards.map((shard) => (
					<Link
						key={shard.id}
						to={`/servers/${shard.server?.slug}/shards/${shard.shard?.slug}/players`}
						className="group flex items-center gap-3 rounded-xl border bg-muted/30 p-4 transition-colors hover:bg-muted/50"
					>
						{shard.server?.logo && (
							<img src={shard.server.logo} alt="" className="h-8 shrink-0 md:h-10" />
						)}
						<div className="min-w-0">
							<h3 className="font-semibold text-sm group-hover:underline md:text-base truncate">
								{shard.server?.name}
							</h3>
							<p className="text-xs text-muted-foreground truncate">{shard.shard?.name}</p>
						</div>
					</Link>
				))}
			</div>
		</div>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const displayName = data?.owner.name ?? params.username
	const count = data?.userShards.length ?? 0
	return [
		{ title: `${displayName}'s Shards | ${brandName}` },
		{ name: 'description', content: `${displayName}'s ${count} shard${count !== 1 ? 's' : ''} on ${brandName}` },
	]
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => <p>No user with the username "{params.username}" exists</p>,
			}}
		/>
	)
}
